import axios, { AxiosInstance } from "axios";
import Store from "./store";
import { signOut } from "./auth/authActions";
import { clearMenuData } from "./menu/menuAction";
import { showErrorToast } from "../util/toastUtils";
import { authStorage } from "../util/authStorage";
import { CREDENTIALS, MERCHANT_PORTAL } from "../shared/constants";

// Logout flag (to avoid toast on manual logout) 
let isUserInitiatedLogout = false;

export const setUserInitiatedLogout = (value: boolean) => {
  isUserInitiatedLogout = value;
};

// Shared Interceptors
const attachInterceptors = (instance: AxiosInstance): AxiosInstance => {
  /* ---------------- Request Interceptor ---------------- */
  instance.interceptors.request.use((config) => {
    config.headers = config.headers ?? {};

    // Static header
    config.headers["merchant-portal"] = MERCHANT_PORTAL;

    // Add staffId to request for all methods
    try {
      const credentials = Store.getState()?.auth?.credentials;
      if (credentials && (credentials as any).id) {
        const staffId = (credentials as any).id;

        if (config.method?.toLowerCase() === 'get') {
          config.params = config.params || {};
          config.params.staffId = staffId;
        } else {
          if (config.method?.toLowerCase() !== 'get') {
            let hasStaffId = false;
            let hasEncryptedPayload = false;

            if (config.data && typeof config.data === 'object') {
              if (config.data.staffId) {
                hasStaffId = true;
              }
              // Check if this is an encrypted payload (when data contains a 'data' property that is a string)
              if (config.data.data && typeof config.data.data === 'string') {
                hasEncryptedPayload = true;
              }
            } else if (config.data && typeof config.data === 'string') {
              // If the entire data is a string, it's likely an encrypted payload
              hasEncryptedPayload = true;
            }

            // Only add staffId to non-encrypted payloads
            if (!hasEncryptedPayload && !hasStaffId) {
              if (config.data && typeof config.data === 'object') {
                config.data.staffId = staffId;
              } else {
                config.data = { staffId };
              }
            }
            // For encrypted payloads, staffId should already be included in the encrypted data by the calling function
            // The calling function is responsible for including staffId in the encrypted payload when needed
          }
        }
      }
    } catch (error) {
      console.error("Error adding staffId to request:", error);
    }

    return config;
  });

  /* ---------------- Response Interceptor ---------------- */
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;

      if (status) {
        switch (status) {
          case 401:
            if (!isUserInitiatedLogout) {
              showErrorToast("Token expired! Please log in again.");
            }
            Store.dispatch(signOut());
            Store.dispatch(clearMenuData());
            authStorage.clearAll();
            break;

          case 403:
            return error.response;

          case 400:
          case 409:
          case 500:
            return error.response;

          default:
            break;
        }
      }

      throw error;
    }
  );

  return instance;
};

// Axios Factory
const createAPI = (baseURL?: string, timeout = 60_000): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    timeout,
    withCredentials: true,
  });

  return attachInterceptors(instance);
};

// API Instances
const API = createAPI(process.env.REACT_APP_API_ENDPOINT);
const Image_API = createAPI(process.env.REACT_APP_IMAGE_DOMAIN);
const API_DISPLAY = createAPI(process.env.REACT_APP_API_ENDPOINT_DISPLAY);
const REPORTS_API = createAPI(process.env.REACT_APP_API_ENDPOINT_REPORTS);

/**
 * Establish HttpOnly cookie session from stored tokens.
 * Called after login and on every page refresh.
 * HttpOnly cookies are sent automatically via withCredentials.
 * Returns session data: { id, merchantId, locationId, businessName, topicToSubscribe, userId, accessToken, refreshToken }
 */
export const getSessionContext = () => {
  return API({
    method: "get",
    url: "/merchants/session/context",
  });
};

export { API, Image_API, API_DISPLAY, REPORTS_API };
